import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast as sonnerToast } from "sonner";
import { Agenda } from "./components/agenda";
import { HorarioMedico } from "./components/horarioMedico";
import { ListaAgenda } from "./components/listaAgenda";
import { ListaPaciente } from "./components/listaPaciente";
import { NovoAgendamento } from "./components/novoAgendamento";
import { useAgendaRealtime } from "./hooks/useAgendaRealtime";

// Dados mock para testes
interface MockAppointment {
  id: number;
  id_paciente: number;
  paciente_nome?: string;
  id_tipo_agenda: number;
  tipo_agenda_descricao?: string;
  id_medico: number;
  medico_nome?: string;
  data_agendamento: string;
  hora_inicio: string;
  hora_fim: string;
  sala_consultorio?: string;
  motivo?: string;
  observacoes?: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status_agendamento: 'AGENDADO' | 'CONFIRMADO' | 'CANCELADO' | 'AGUARDANDO_CONFIRMACAO' | 'CONCLUIDO';
  duracao_minutos: number;
  meet_link?: string;
  notificacao_sms: boolean;
  notificacao_whatsapp: boolean;
  notificacao_email: boolean;
}

interface TipoAtendimentoAgenda {
  id: number;
  nome: string;
  cor?: string;
  habilitado_meet?: boolean;
}

// Função auxiliar para normalizar horário (remove segundos se forem :00)
const normalizarHorario = (horario: string): string => {
  if (!horario) return horario;
  // Se o horário tem formato HH:MM:SS e os segundos são :00, remove os segundos
  if (horario.match(/^\d{2}:\d{2}:00$/)) {
    return horario.substring(0, 5); // Retorna apenas HH:MM
  }
  return horario;
};

const ordenarAgendamentos = (itens: MockAppointment[]): MockAppointment[] => {
  return [...itens].sort((a, b) => {
    const dataCompare = a.data_agendamento.localeCompare(b.data_agendamento);
    if (dataCompare !== 0) return dataCompare;
    return a.hora_inicio.localeCompare(b.hora_inicio);
  });
};

const Agendamentos = () => {
  // Estado para agendamentos da API
  const [appointments, setAppointments] = useState<MockAppointment[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPatientDialogOpen, setIsPatientDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<MockAppointment | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<{ id: number; name: string; cpf: string } | null>(null);
  const [filterDoctor, setFilterDoctor] = useState<string[]>([]); // Array para múltiplos médicos
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [viewType, setViewType] = useState<"diaria" | "semanal">("diaria");
  const [periodStart, setPeriodStart] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [activeTab, setActiveTab] = useState<string>("lista");


  const handleSaveAppointment = async (appointmentData: MockAppointment | Omit<MockAppointment, 'id'>) => {
    try {
      if ('id' in appointmentData) {
        // Atualizar agendamento existente
        await updateAppointment(appointmentData.id, appointmentData);
      } else {
        // Criar novo agendamento
        await addAppointment(appointmentData);
      }
      setSelectedAppointment(null);
      // Não precisa recarregar aqui, pois as funções já recarregam
    } catch (error) {
      // Erro já foi tratado nas funções addAppointment/updateAppointment
    }
  };

  const handleOpenAppointment = (appointment: MockAppointment) => {
    setSelectedAppointment(appointment);
    // Se o agendamento tem paciente, define o paciente selecionado
    if (appointment.paciente_nome) {
      setSelectedPatient({
        id: appointment.id_paciente,
        name: appointment.paciente_nome,
        cpf: "", // CPF não está disponível no MockAppointment
      });
    }
    setIsDialogOpen(true);
  };

  const handleOpenNewAppointment = () => {
    setSelectedAppointment(null);
    setSelectedPatient(null);
    setIsPatientDialogOpen(true);
  };

  const handleSelectPatient = (patient: { id: number; name: string; cpf: string }) => {
    setSelectedPatient(patient);
    setIsPatientDialogOpen(false);
    setIsDialogOpen(true);
  };

  const handleChangePatient = () => {
    setIsDialogOpen(false);
    setIsPatientDialogOpen(true);
  };


  // Estados para médicos e tipos de agenda
  const [medicos, setMedicos] = useState<Array<{ id: number; nome: string; pessoa?: { nome: string } }>>([]);
  const [tiposAgenda, setTiposAgenda] = useState<Array<{ id: number; descricao: string; cor?: string }>>([]);
  const [tiposAtendimento, setTiposAtendimento] = useState<TipoAtendimentoAgenda[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [diasSemanaConfigurados, setDiasSemanaConfigurados] = useState<string[]>([]);
  const [googleMeetAutoEnabled, setGoogleMeetAutoEnabled] = useState(false);

  // Função para mapear dados da API para o formato do frontend
  const mapearAgendamento = useCallback((agenda: any): MockAppointment => {
    return {
      id: agenda.id,
      id_paciente: agenda.id_paciente,
      paciente_nome: agenda.paciente?.pessoa?.nome || agenda.paciente_nome || '',
      id_tipo_agenda: agenda.id_tipo_atendimento || agenda.id_tipo_agenda, // Mantém compatibilidade
      tipo_agenda_descricao: agenda.tipoAtendimento?.nome || agenda.tipoAgenda?.descricao || agenda.tipo_agenda_descricao || '',
      id_medico: agenda.id_medico,
      medico_nome: agenda.medico?.pessoa?.nome || agenda.medico_nome || '',
      data_agendamento: typeof agenda.data_agendamento === 'string'
        ? agenda.data_agendamento.split('T')[0]
        : format(new Date(agenda.data_agendamento), 'yyyy-MM-dd'),
      hora_inicio: normalizarHorario(agenda.hora_inicio),
      hora_fim: normalizarHorario(agenda.hora_fim),
      motivo: agenda.motivo,
      observacoes: agenda.observacoes,
      prioridade: agenda.prioridade || 'MEDIA',
      status_agendamento: agenda.status_agendamento || 'AGENDADO',
      duracao_minutos: agenda.duracao_minutos || 30,
      meet_link: agenda.meet_link || undefined,
      notificacao_sms: agenda.notificacao_sms || false,
      notificacao_whatsapp: agenda.notificacao_whatsapp || false,
      notificacao_email: agenda.notificacao_email || false,
    };
  }, []);

  const upsertAgendamentos = useCallback((novos: MockAppointment[]) => {
    if (novos.length === 0) return;

    setAppointments((anteriores) => {
      const porId = new Map(anteriores.map((item) => [item.id, item]));

      for (const item of novos) {
        porId.set(item.id, item);
      }

      return ordenarAgendamentos(Array.from(porId.values()));
    });
  }, []);

  const removerAgendamentoLocal = useCallback((agendaId: number) => {
    setAppointments((anteriores) =>
      anteriores.filter((item) => item.id !== agendaId)
    );
  }, []);

  // Função para buscar agendamentos da API
  const carregarAgendamentos = useCallback(async () => {
    setIsLoadingAppointments(true);
    try {
      const response = await api.get('/api/agenda');
      if (response?.result) {
        const agendamentosMapeados = Array.isArray(response.result)
          ? response.result.map(mapearAgendamento)
          : [];
        setAppointments(ordenarAgendamentos(agendamentosMapeados));
      } else {
        setAppointments([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar agendamentos:', error);
      sonnerToast.error('Erro ao carregar agendamentos', {
        description: error.message || 'Não foi possível carregar os agendamentos'
      });
      setAppointments([]);
    } finally {
      setIsLoadingAppointments(false);
    }
  }, [mapearAgendamento]);

  // Funções para gerenciar agendamentos via API
  const addAppointment = async (appointment: Omit<MockAppointment, 'id'>) => {
    try {
      // Garante que a data está no formato correto
      const dataAgendamento = appointment.data_agendamento;

      const payload = {
        id_paciente: Number(appointment.id_paciente),
        id_medico: Number(appointment.id_medico),
        id_tipo_atendimento: Number((appointment as any).id_tipo_atendimento || appointment.id_tipo_agenda),
        data_agendamento: dataAgendamento, // Envia como string YYYY-MM-DD
        hora_inicio: appointment.hora_inicio,
        hora_fim: appointment.hora_fim,
        duracao_minutos: appointment.duracao_minutos || 30,
        motivo: appointment.motivo || undefined,
        observacoes: appointment.observacoes || undefined,
        prioridade: appointment.prioridade || 'MEDIA',
        status_agendamento: appointment.status_agendamento || 'AGENDADO',
      };

      console.log('Enviando agendamento:', payload);

      const response = await api.post('/api/agenda', payload);

      if (response?.result) {
        sonnerToast.success('Agendamento criado com sucesso!');
        // Recarrega os agendamentos para garantir dados atualizados
        await carregarAgendamentos();
      }
    } catch (error: any) {
      console.error('Erro ao criar agendamento:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Não foi possível criar o agendamento';
      sonnerToast.error('Erro ao criar agendamento', {
        description: errorMessage
      });
      throw error;
    }
  };

  const updateAppointment = async (id: number, updates: Partial<MockAppointment>) => {
    try {
      // Busca o agendamento atual para preencher campos não fornecidos
      const agendamentoAtual = appointments.find(apt => apt.id === id);
      if (!agendamentoAtual) {
        throw new Error('Agendamento não encontrado');
      }

      const response = await api.put(`/api/agenda/${id}`, {
        id_paciente: updates.id_paciente ?? agendamentoAtual.id_paciente,
        id_medico: updates.id_medico ?? agendamentoAtual.id_medico,
        id_tipo_atendimento: (updates as any).id_tipo_atendimento ?? (agendamentoAtual as any).id_tipo_atendimento ?? agendamentoAtual.id_tipo_agenda,
        data_agendamento: updates.data_agendamento ?? agendamentoAtual.data_agendamento,
        hora_inicio: updates.hora_inicio ?? agendamentoAtual.hora_inicio,
        hora_fim: updates.hora_fim ?? agendamentoAtual.hora_fim,
        duracao_minutos: updates.duracao_minutos ?? agendamentoAtual.duracao_minutos,
        motivo: updates.motivo ?? agendamentoAtual.motivo,
        observacoes: updates.observacoes ?? agendamentoAtual.observacoes,
        prioridade: updates.prioridade ?? agendamentoAtual.prioridade,
        status_agendamento: updates.status_agendamento ?? agendamentoAtual.status_agendamento,
      });

      if (response?.result) {
        sonnerToast.success('Agendamento atualizado com sucesso!');
        // Recarrega os agendamentos para garantir dados atualizados
        await carregarAgendamentos();
      }
    } catch (error: any) {
      console.error('Erro ao atualizar agendamento:', error);
      sonnerToast.error('Erro ao atualizar agendamento', {
        description: error.message || 'Não foi possível atualizar o agendamento'
      });
      throw error;
    }
  };

  const deleteAppointment = async (id: number) => {
    try {
      await api.delete(`/api/agenda/${id}`);
      sonnerToast.success('Agendamento excluído com sucesso!');
      // Recarrega os agendamentos para garantir dados atualizados
      await carregarAgendamentos();
    } catch (error: any) {
      console.error('Erro ao excluir agendamento:', error);
      sonnerToast.error('Erro ao excluir agendamento', {
        description: error.message || 'Não foi possível excluir o agendamento'
      });
      throw error;
    }
  };

  const createManualMeetLink = async (id: number) => {
    try {
      const response = await api.post(`/api/agenda/${id}/meet-link`, {});
      if (response?.result) {
        sonnerToast.success('Link do Google Meet criado com sucesso!');
        await carregarAgendamentos();
      }
    } catch (error: any) {
      console.error('Erro ao criar link manual do Meet:', error);
      sonnerToast.error('Erro ao criar link do Meet', {
        description:
          error?.response?.data?.error ||
          error?.message ||
          'Não foi possível criar o link do Google Meet.'
      });
    }
  };

  useAgendaRealtime<MockAppointment>({
    mapApiItem: mapearAgendamento,
    onUpsertMany: upsertAgendamentos,
    onDelete: removerAgendamentoLocal,
    onFullSync: carregarAgendamentos,
  });

  // Buscar médicos, tipos de agenda e tipos de atendimento da API
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const userStorage =
          localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
        const user = userStorage ? JSON.parse(userStorage) : null;
        const idEmpresa = Number(user?.id_empresa || 0);

        if (Number.isInteger(idEmpresa) && idEmpresa > 0) {
          try {
            const meetConfigResponse = await api.get(
              `/api/empresas/${idEmpresa}/google-meet-config`
            );
            setGoogleMeetAutoEnabled(Boolean(meetConfigResponse?.result?.habilitado));
          } catch {
            setGoogleMeetAutoEnabled(false);
          }
        } else {
          setGoogleMeetAutoEnabled(false);
        }

        // Buscar médicos
        const medicosResponse = await api.get('/api/medicos');
        if (medicosResponse?.result) {
          setMedicos(medicosResponse.result);
        }

        // Buscar tipos de agenda (apenas os habilitados)
        const tiposResponse = await api.get('/api/tipo_agenda');
        if (tiposResponse?.result) {
          // Filtrar apenas os tipos habilitados para agenda
          const tiposHabilitados = tiposResponse.result.filter(
            (tipo: any) => tipo.habilitado_agenda !== false
          );
          setTiposAgenda(tiposHabilitados);
        }

        // Buscar tipos de atendimento (apenas os habilitados para agenda)
        const tiposAtendimentoResponse = await api.get('/api/tipo_atendimento');
        if (tiposAtendimentoResponse?.result) {
          // Filtrar apenas os tipos habilitados para agenda (habilitado_agenda === true)
          const tiposHabilitados = Array.isArray(tiposAtendimentoResponse.result)
            ? tiposAtendimentoResponse.result.filter(
              (tipo: any) => tipo.habilitado_agenda === true
            )
            : tiposAtendimentoResponse.result.data
              ? tiposAtendimentoResponse.result.data.filter(
                (tipo: any) => tipo.habilitado_agenda === true
              )
              : [];
          setTiposAtendimento(tiposHabilitados);
        }

        // Buscar agendamentos
        await carregarAgendamentos();
      } catch (error: any) {
        sonnerToast.error('Erro ao carregar dados', {
          description: error.message || 'Não foi possível carregar médicos e tipos de agenda'
        });
      }
    };

    carregarDados();
  }, [carregarAgendamentos]);

  // Função para buscar horários disponíveis baseado nos médicos selecionados (memoizada para evitar loops)
  const buscarHorariosDisponiveis = useCallback(async (idsMedicos: number[] | null, data: string) => {
    if (!data) {
      setAvailableTimeSlots([]);
      return;
    }

    try {
      // Se idsMedicos for null ou vazio, busca de todos os médicos
      const url = idsMedicos && idsMedicos.length > 0
        ? `/api/horarios_medicos/disponiveis?ids_medicos=${idsMedicos.join(',')}&data=${data}`
        : `/api/horarios_medicos/disponiveis?data=${data}`;

      const response = await api.get(url);

      if (response?.result) {
        // Extrai apenas os horários de início, normaliza e ordena
        const horarios = response.result
          .map((h: { hora_inicio: string }) => normalizarHorario(h.hora_inicio))
          .sort();
        setAvailableTimeSlots(horarios);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error: any) {
      console.error('Erro ao buscar horários disponíveis:', error);
      setAvailableTimeSlots([]);
    }
  }, []); // Array vazio porque não depende de nenhum estado

  // Função para buscar dias da semana configurados
  const buscarDiasSemanaConfigurados = async (idsMedicos: number[] | null) => {
    try {
      const url = idsMedicos && idsMedicos.length > 0
        ? `/api/horarios_medicos/dias_semana?ids_medicos=${idsMedicos.join(',')}`
        : `/api/horarios_medicos/dias_semana`;

      const response = await api.get(url);

      if (response?.result) {
        setDiasSemanaConfigurados(response.result);
      } else {
        setDiasSemanaConfigurados([]);
      }
    } catch (error: any) {
      console.error('Erro ao buscar dias da semana configurados:', error);
      setDiasSemanaConfigurados([]);
    }
  };

  // Atualiza horários quando os médicos selecionados ou a data mudam
  useEffect(() => {
    if (selectedDate) {
      // Garante que a data está no formato correto (sem problemas de fuso horário)
      const dataFormatada = format(selectedDate, 'yyyy-MM-dd');

      // Se há médicos selecionados, converte nomes para IDs
      // Se não há médicos selecionados, busca de todos os médicos
      let idsMedicos: number[] | null = null;

      if (filterDoctor.length > 0) {
        idsMedicos = medicos
          .filter(m => {
            const nomeMedico = m.pessoa?.nome || m.nome || '';
            return filterDoctor.includes(nomeMedico);
          })
          .map(m => m.id);

        // Se não encontrou nenhum médico válido, busca de todos
        if (idsMedicos.length === 0) {
          idsMedicos = null;
        }
      }

      buscarHorariosDisponiveis(idsMedicos, dataFormatada);
      buscarDiasSemanaConfigurados(idsMedicos);
    } else {
      setAvailableTimeSlots([]);
      setDiasSemanaConfigurados([]);
    }
  }, [filterDoctor, selectedDate, medicos]);

  const doctors = medicos.map(m => m.pessoa?.nome || m.nome || '').filter(Boolean);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Agendamentos</h1>
            <p className="text-muted-foreground">Gerencie os agendamentos de consultas e exames</p>
          </div>
          {activeTab !== "horarios" && (
            <Button className="gap-2" onClick={handleOpenNewAppointment}>
              <Plus className="h-4 w-4" />
              Novo Agendamento
            </Button>
          )}
        </div>

        <Tabs defaultValue="lista" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="lista">Lista</TabsTrigger>
            <TabsTrigger value="agenda">Agenda</TabsTrigger>
            <TabsTrigger value="horarios">Horários Médicos</TabsTrigger>
          </TabsList>

          <TabsContent value="lista" className="space-y-4">
            <ListaAgenda
              appointments={appointments}
              doctors={doctors}
              filterDoctor={filterDoctor}
              filterStatus={filterStatus}
              filterType={filterType}
              tiposAtendimento={tiposAtendimento}
              isGoogleMeetAutoEnabled={googleMeetAutoEnabled}
              onFilterDoctorChange={setFilterDoctor}
              onFilterStatusChange={setFilterStatus}
              onFilterTypeChange={setFilterType}
              onUpdateAppointment={updateAppointment}
              onCreateMeetLink={createManualMeetLink}
              onDeleteAppointment={deleteAppointment}
            />
          </TabsContent>
          <TabsContent value="agenda" className="space-y-4">
            <Agenda
              appointments={appointments}
              doctors={doctors}
              filterDoctor={filterDoctor}
              filterStatus={filterStatus}
              filterType={filterType}
              viewType={viewType}
              selectedDate={selectedDate}
              periodStart={periodStart}
              availableTimeSlots={availableTimeSlots}
              diasSemanaConfigurados={diasSemanaConfigurados}
              tiposAgenda={tiposAgenda}
              tiposAtendimento={tiposAtendimento}
              isGoogleMeetAutoEnabled={googleMeetAutoEnabled}
              onFilterDoctorChange={setFilterDoctor}
              onFilterStatusChange={setFilterStatus}
              onFilterTypeChange={setFilterType}
              onViewTypeChange={setViewType}
              onPeriodStartChange={setPeriodStart}
              onSelectedDateChange={setSelectedDate}
              onAppointmentClick={handleOpenAppointment}
              onCreateMeetLink={createManualMeetLink}
            />
          </TabsContent>

          <TabsContent value="horarios" className="space-y-6">
            <HorarioMedico />
          </TabsContent>

        </Tabs>
      </div>

      <ListaPaciente
        isOpen={isPatientDialogOpen}
        onOpenChange={setIsPatientDialogOpen}
        onSelectPatient={handleSelectPatient}
      />

      <NovoAgendamento
        isOpen={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelectedAppointment(null);
            if (!selectedAppointment) {
              setSelectedPatient(null);
            }
          }
        }}
        appointment={selectedAppointment}
        selectedPatient={selectedPatient}
        onChangePatient={handleChangePatient}
        medicos={medicos}
        tiposAgenda={tiposAgenda}
        tiposAtendimento={tiposAtendimento}
        availableTimeSlots={availableTimeSlots}
        appointments={appointments}
        onSave={handleSaveAppointment}
        onBuscarHorarios={buscarHorariosDisponiveis}
        onDelete={deleteAppointment}
      />
    </Layout>
  );
};

export default Agendamentos;
